import React, { useState } from 'react';
import translations from './practiceplaceprofile.i18n';
import { useIntl } from 'react-intl';
import { PraksisstedprofilDto } from '../../api/generated/praksissted-generated-api';
import Button from '../../components/button';
import PageLayout from '../../pagelayout';
import { Grid } from '@mui/material';
import * as css from './practiceplaceprofile.module.scss';
import classnames from 'classnames';
import Icon, { IconType } from '../../components/icon';
import PanelHeader from '../../components/panelheader/panelHeader';
import { PraksisstedInfoPutDto } from '../../api/generated/studiested-generated-api';
import { NavLink } from 'react-router-dom';
import { formatUrlNoScheme } from '../utils';
import {
  ActionItems,
  Bilde,
  EditProfileModal,
  LastEditedBy,
  hasLastEditedByInfo,
} from './commoncomponents';
import { reduceToEditProfilType } from './practiceplaceprofileutils';

interface PracticePlaceProfileProps {
  data: PraksisstedprofilDto;
  menuItems: React.ReactNode;
  canEdit: boolean;
  isAdmin: boolean;
  saveHandler: (beskrivelse: string, krav: string) => void;
  saveContactInfo: (updatedPraksissted: PraksisstedInfoPutDto) => void;
}

const PracticePlaceProfilePsa: React.FC<
  React.PropsWithChildren<PracticePlaceProfileProps>
> = ({ data, menuItems, canEdit, isAdmin, saveHandler, saveContactInfo }) => {
  const { formatMessage } = useIntl();

  const cancelEdit = () => {
    setIsEditMode(false);
  };

  const saveEdit = () => {
    saveHandler(beskrivelse, krav);
    setIsEditMode(false);
  };

  const labels = {
    title: formatMessage(
      { id: translations.title.id },
      { practicePlaceName: data.navn },
    ),
    subtitle: formatMessage({ id: translations.subtitle.id }),
    profileImgName: formatMessage(
      { id: translations.profileImgName.id },
      { practiceplaceName: data.navn },
    ),
    eier: formatMessage({ id: translations.eier.id }),
    orgnr: formatMessage({ id: translations.orgnr.id }),
    kontaktinfo: formatMessage({ id: translations.kontaktinfo.id }),
    epost: formatMessage({ id: translations.epost.id }),
    telefon: formatMessage({ id: translations.telefon.id }),
    nettside: formatMessage({ id: translations.nettside.id }),
    beskrivelse: formatMessage({ id: translations.beskrivelse.id }),
    krav: formatMessage({ id: translations.krav.id }),
    praksissteder: formatMessage({ id: translations.praksissteder.id }),
  };

  const [isEditMode, setIsEditMode] = useState(false);
  const [beskrivelse, setBeskrivelse] = useState(data.beskrivelse || '');
  const [krav, setKrav] = useState(data.krav || '');
  const [isOpen, setIsOpen] = useState(false);

  const actionItems = (
    <ActionItems
      backPath={'/myorganization/departments'}
      isEditMode={isEditMode}
      setIsEditMode={setIsEditMode}
      canEdit={canEdit}
      cancelEdit={cancelEdit}
      saveEdit={saveEdit}
    />
  );

  return (
    <PageLayout
      isIndented={true}
      title={labels.title}
      subtitle={labels.subtitle}
      menuItems={menuItems}
      actionItems={actionItems}
    >
      <EditProfileModal
        data={reduceToEditProfilType(data)}
        isOpen={isOpen}
        handleSaveContactInfo={saveContactInfo}
        handleClose={() => setIsOpen(false)}
      />
      <Grid container className={css.container} alignItems={'stretch'}>
        <Grid item xs={12} sm={12} md={8} lg={7} xl={6}>
          <Bilde
            bildeFileId={data.bildeId}
            bildeFilename={labels.profileImgName}
            praksisstedId={data.id}
            isEditMode={isEditMode}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={4} lg={5} xl={6} className={css.info}>
          <div
            className={classnames(
              css.label,
              css.practicePlaceName,
              'practiceplacename',
            )}
          >
            {data.navn}
          </div>
          {data.orgnr && (
            <div className={css.orgnr}>
              <span className={css.label}>{labels.orgnr}:</span>{' '}
              <span className="organizationNumber">{data.orgnr}</span>
            </div>
          )}
          {data.avdeling && (
            <div>
              <span className={css.label}>{labels.eier}:</span>{' '}
              <NavLink
                to={`/myorganization/practiceplaces/profile/${data.avdeling.id}`}
              >
                <span className="departmentName">{data.avdeling.navn}</span>
              </NavLink>
            </div>
          )}
          <PanelHeader title={labels.kontaktinfo} backgroundColor={'#ffffff'} />
          {data.adresse && (
            <>
              {data.adresse.adresselinje1 && (
                <div className="streetAddress">
                  {data.adresse.adresselinje1}
                </div>
              )}
              {data.adresse.adresselinje2 && (
                <div className="streetAddress">
                  {data.adresse.adresselinje2}
                </div>
              )}
              <div className="postalAddress">
                {data.adresse.postnr} {data.adresse.poststed}
              </div>
            </>
          )}
          {data.nettsideUrl && (
            <div>
              <span className={css.label}>{labels.nettside}:</span>{' '}
              <a href={data.nettsideUrl} className="websiteUrl">
                {formatUrlNoScheme(data.nettsideUrl)}
              </a>
            </div>
          )}
          {data.epost && (
            <div>
              <span className={css.label}>{labels.epost}:</span>{' '}
              <a href={`mailto: ${data.epost}`} className="email">
                {data.epost}
              </a>
            </div>
          )}
          {data.telefonnummer && (
            <div>
              <span className={css.label}>{labels.telefon}:</span>{' '}
              <span className="phone">{data.telefonnummer}</span>
            </div>
          )}
          {isEditMode && isAdmin && (
            <div className={css.editProfileButtonContainer}>
              <Button.Secondary onClick={() => setIsOpen(true)}>
                {formatMessage({ id: translations.endreProfil.id })}
              </Button.Secondary>
            </div>
          )}
        </Grid>
      </Grid>
      <Grid container={true} className={css.container}>
        {(data.beskrivelse || isEditMode) && (
          <Grid item={true} xs={12}>
            <PanelHeader title={labels.beskrivelse} />
            {isEditMode ? (
              <textarea
                value={beskrivelse}
                onChange={(e) => setBeskrivelse(e.target.value)}
              />
            ) : (
              <p>{data.beskrivelse}</p>
            )}
          </Grid>
        )}
        {(data.beskrivelse || isEditMode) && (
          <Grid item={true} xs={12}>
            <PanelHeader title={labels.krav} />
            {isEditMode ? (
              <textarea
                value={krav}
                onChange={(e) => setKrav(e.target.value)}
              />
            ) : (
              <p>{data.krav}</p>
            )}
          </Grid>
        )}
        {data.noder.under && data.noder.under.length > 0 && (
          <Grid item={true} xs={12}>
            <PanelHeader title={labels.praksissteder} />
            <Grid
              container={true}
              spacing={5}
              className={css.praksisstedCardWrapper}
            >
              {data.noder.under.map((praksissted) => (
                <Grid
                  item={true}
                  xs={12}
                  md={6}
                  lg={4}
                  xl={3}
                  key={praksissted.id}
                >
                  <PraksisstedProfileCard
                    id={praksissted.id}
                    name={praksissted.navn}
                    bilde={praksissted.bilde}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
        )}
        {hasLastEditedByInfo(data) && (
          <>
            <Grid item={true} xs={7}>
              <LastEditedBy
                className={css.lastEditedBy}
                praksisstedprofil={data}
              />
            </Grid>
            <Grid item={true} xs={5} />
          </>
        )}
      </Grid>
    </PageLayout>
  );
};

interface PraksisstedProfileCardProps {
  id: string;
  name: string;
  bilde: string;
}

const PraksisstedProfileCard: React.FC<
  React.PropsWithChildren<PraksisstedProfileCardProps>
> = ({ id, name, bilde }) => {
  const { formatMessage } = useIntl();
  return (
    <div className={css.praksissted}>
      {bilde && (
        <div className={css.profilePicture} aria-hidden="true">
          <img src={bilde} alt="" />
        </div>
      )}
      {!bilde && (
        <div className={css.replacementPicture}>
          <div className={css.iconWrapper}>
            <Icon type={IconType.EndNode} title={''} htmlId={id} />
          </div>
        </div>
      )}
      <div className={css.text}>
        <h3 className={css.avdelingsnavn}>{name}</h3>
        <div className={css.readMoreLink}>
          <NavLink to={`/myorganization/practiceplaces/profile/${id}`}>
            {formatMessage({ id: translations.lesMer.id })}
          </NavLink>
        </div>
      </div>
    </div>
  );
};

export default PracticePlaceProfilePsa;
